/* eslint-disable no-underscore-dangle */
import uuid from 'uuid';
import axios from 'axios';
import {
  VIEW_CONTENT,
  ADD_TO_CART,
  INITIATE_CHECKOUT,
  PURCHASE,
  COMPLETE_PAYMENT,
  PAGE_VISIT,
  CHECKOUT,
} from 'shared/constants/social-media';

import { hashData } from './utils';

/**
 * Shapes product data for social media server events
 * @param {string} action
 * @param {string} eventId
 * @return {object}
 */
const productDataForServer = (
  action,
  eventId,
  { product, currency, bag, customerDetails = {}, fbp, fbc }
) => {
  let shapedData = {};

  const customerInformationParameters = {
    ...(typeof navigator !== 'undefined' &&
      navigator.userAgent && {
        client_user_agent: navigator.userAgent,
      }),
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
  };

  if (customerDetails) {
    if (customerDetails.firstName) {
      customerInformationParameters.fn = hashData(
        customerDetails.firstName.trim()
      );
    }
    if (customerDetails.lastName) {
      customerInformationParameters.ln = hashData(
        customerDetails.lastName.trim()
      );
    }
    if (customerDetails.email) {
      customerInformationParameters.em = hashData(
        customerDetails.email.trim().toLowerCase()
      );
    }
  }

  const serverEventParameters = {
    event_name: action === COMPLETE_PAYMENT ? PURCHASE : action,
    event_id: eventId,
    action_source: 'website',
    event_source_url:
      typeof window !== 'undefined' ? window.location?.href : undefined,
    event_time: Math.floor(Date.now() / 1000),
  };

  const customDataBase = {
    content_type: 'product',
    currency,
  };

  if (action === ADD_TO_CART || action === VIEW_CONTENT) {
    shapedData = {
      ...serverEventParameters,
      user_data: customerInformationParameters,
      custom_data: {
        ...customDataBase,
        value: product?.prices?.price,
        content_ids: [product?.styleColourCode].filter(Boolean),
        contents: [
          {
            id: product?.styleColourCode,
            quantity: 1,
            item_price: product?.prices?.price,
          },
        ],
      },
    };
  }

  if (action === INITIATE_CHECKOUT || action === COMPLETE_PAYMENT) {
    const items = Array.isArray(bag?.items) ? bag.items : [];
    shapedData = {
      ...serverEventParameters,
      user_data: customerInformationParameters,
      custom_data: {
        ...customDataBase,
        contents: items.map(item => ({
          id: item.skuCode,
          quantity: item.quantity,
          item_price: item.price,
        })),
        content_ids: items.map(item => item.styleColourCode).filter(Boolean),
        value: bag?.subTotal,
      },
    };
  }

  return shapedData;
};

/**
 * Shapes product data for social media browser events (FB, TikTok, Pinterest)
 * @param {string} channel
 * @param {string} action
 * @return {object}
 */
const productDataForBrowser = (
  channel,
  action,
  { product, quantity, currency, bag }
) => {
  let shapedData;

  if (action === VIEW_CONTENT || action === ADD_TO_CART) {
    const fbProps = {
      content_ids: [product?.styleColourCode],
      value: product?.prices?.price * (action === ADD_TO_CART ? quantity : 1),
      content_category: product?.category,
      content_type: 'product',
      content_name: product?.styleColourName,
    };

    const ttProps = {
      content_ids: [product?.styleColourCode],
      value:
        action === ADD_TO_CART
          ? product?.prices?.price * quantity
          : product?.prices?.price,
      ...(action === ADD_TO_CART && { quantity }),
      content_category: product?.category,
      content_type: 'product',
      contents: [
        {
          content_id: product?.styleColourCode,
          content_name: product?.styleColourName,
          content_category: product?.category,
          price: product?.prices?.price,
          quantity: action === ADD_TO_CART ? quantity : 1,
        },
      ],
    };

    const pinterestProps = {
      product_id: product?.styleColourCode,
      value: product?.prices?.price * (action === ADD_TO_CART ? quantity : 1),
      product_category: product?.category,
      product_name: product?.styleColourName,
    };

    const variedProperties = {
      ...(channel === 'fb' && fbProps),
      ...(channel === 'tt' && ttProps),
      ...(channel === 'pinterest' && pinterestProps),
    };

    shapedData = {
      ...variedProperties,
      currency,
    };
  }

  if (action === INITIATE_CHECKOUT) {
    const items = Array.isArray(bag?.items) ? bag.items : [];

    shapedData = {
      currency,
      value: bag?.subTotal,
      contents: items.map(item => {
        return {
          ...(channel === 'fb' && { id: item.skuCode }),
          ...(channel === 'tt' && {
            content_id: item.skuCode,
            price: item.price,
          }),
          quantity: item.quantity,
        };
      }),
      ...(channel === 'fb' && { num_items: bag?.itemCount }),
      ...(channel === 'tt' && {
        quantity: bag?.itemCount,
        content_ids: items.map(item => item.styleColourCode).filter(Boolean),
      }),
    };
  }

  if (action === COMPLETE_PAYMENT || action === PURCHASE) {
    const items = Array.isArray(bag?.items) ? bag.items : [];

    const fbProps = {
      contents: items.map(item => ({
        id: item.skuCode,
        quantity: item.quantity,
        item_price: item.price,
      })),
      quantity: bag?.itemCount,
    };

    const ttProps = {
      contents: items.map(item => ({
        content_id: item.styleColourCode,
        content_name: item.styleName,
        quantity: item.quantity,
        price: item.price,
      })),
      content_ids: items.map(item => item.styleColourCode).filter(Boolean),
      quantity: bag?.itemCount,
    };

    const pinterestProps = {
      line_items: items.map(item => ({
        product_id: item.styleColourCode,
        product_quantity: item.quantity,
        product_category: item.category,
      })),
    };

    const variedProperties = {
      ...(channel === 'fb' && fbProps),
      ...(channel === 'tt' && ttProps),
      ...(channel === 'pinterest' && pinterestProps),
    };

    shapedData = {
      ...variedProperties,
      currency,
      value: bag?.subTotal,
      ...(channel === 'pinterest' && { order_quantity: bag?.itemCount }),
    };
  }
  return shapedData;
};

/**
 * Maps standard actions to pinterest ones
 * @param {string} action
 * @returns {string}
 */
function mapToPinterestAction(action) {
  if (action === VIEW_CONTENT) {
    return PAGE_VISIT;
  }

  if (action === COMPLETE_PAYMENT) {
    return CHECKOUT;
  }

  return action;
}

/**
 * Sends Social Media Data through Browser Events
 * @param {string} channel
 * @param {string} pixelId
 * @param {string} action
 * @param {object} socialMediaData
 * @param {string} eventId
 */
const sendSocialMediaDataThroughBrowser = (
  channel,
  pixelId,
  action,
  socialMediaData,
  eventId
) => {
  if (channel === 'fb' && window.fbq) {
    let ac = action;
    if (ac === COMPLETE_PAYMENT) {
      ac = PURCHASE;
    }
    window.fbq(
      'track',
      ac,
      productDataForBrowser(channel, ac, socialMediaData),
      {
        eventID: eventId,
      }
    );
    return;
  }
  if (channel === 'tt' && window.ttq) {
    if (
      socialMediaData.customerDetails &&
      socialMediaData.customerDetails.email
    ) {
      window.ttq.identify({
        email: socialMediaData.customerDetails.email.trim().toLowerCase(),
      });
    }
    window.ttq.track(
      action,
      productDataForBrowser(channel, action, socialMediaData)
    );
  }
  if (
    channel === 'pinterest' &&
    window.pintrk &&
    (action === ADD_TO_CART ||
      action === VIEW_CONTENT ||
      action === COMPLETE_PAYMENT)
  ) {
    window.pintrk(
      'track',
      mapToPinterestAction(action),
      productDataForBrowser(channel, action, socialMediaData)
    );
  }
};

/**
 * Send Social Media Data through server events
 * @param {string} action
 * @param {Object} data
 * @param {string} eventId
 */
const sendSocialMediaDataThroughServer = async (action, data, eventId) => {
  const shapedData = productDataForServer(action, eventId, data);
  await axios.post(`/api/socialmedia/facebook`, {
    data: shapedData,
    // Born Social use the PURCHASE rather than COMPLETE_PAYMENT so change when required
    event_name: action === COMPLETE_PAYMENT ? PURCHASE : action,
  });
};

/**
 * Send Social Media Data through server events
 * @param {string} action
 * @param {Object} channels
 * @param {Object } data
 */
export async function sendSocialMediaData(action, channels, data) {
  const eventId = uuid.v4().toString().toUpperCase();

  Object.keys(channels).forEach(function eachKey(ch) {
    const pixelId = channels[ch];
    sendSocialMediaDataThroughBrowser(ch, pixelId, action, data, eventId);
  });

  sendSocialMediaDataThroughServer(action, data, eventId);
}
